import React from 'react';
import { injectIntl, FormattedHTMLMessage } from 'react-intl';
import { produce } from 'immer';

import { logger,
         VerificationService,
         StudentPersonalInfoViewModel,
         VerificationStepsEnum,
         getMaxReviewTime,
         getLogoUrl,
         LogoComponent,
         PoweredByComponent,
         FormFooterComponent as FormFooter,
         CountDownComponent,
         SSOPendingComponent,
         usePollingInterval,
         ReviewPendingComponent,
} from '@sheerid/jslib';
import { redirect } from '../../lib/utils/browser';

const DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN = 5000;
const DOC_UPLOAD_POLL_DURATION = 20000;
const SSO_POLL_DURATION = 5000;

interface StepPendingProps {
    verificationService: VerificationService;
    intl: any;
}

const StepPending = ({ verificationService, intl }: StepPendingProps) => {
    const programId = verificationService.programId;
    const viewModel = verificationService.viewModel;
    const previousViewModel = (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
    const previousVerificationResponse = verificationService.previousVerificationResponse || undefined;
    const verificationId = verificationService.verificationResponse.verificationId;
    const hasLogo = getLogoUrl(verificationService.programTheme) ? true : false;
    const countdownSeconds = 20;

    const [showCountDown, setShowCountDown] = React.useState(false);

    const getLogo = () => (
        hasLogo ? (
            <LogoComponent verificationService={verificationService} />
        ) : (
            <img
                className="sid-l-lead-image"
                alt="design element"
                src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
            />
        )
    );

    const isPreviousStepSSO = (): boolean => {
        if (previousVerificationResponse && previousVerificationResponse.currentStep === VerificationStepsEnum.sso) {
            return true;
        }
        return false;
    };

    const pollTime = isPreviousStepSSO()
        ? SSO_POLL_DURATION
        : showCountDown ? DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN : DOC_UPLOAD_POLL_DURATION;

    const stopPollingInterval = usePollingInterval(() => {
        verificationService.fetchExistingVerificationRequest(programId, verificationId, previousVerificationResponse, previousViewModel, false);
    }, pollTime);

    const cancelSSOStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepSSOComponent submitting cancel request');
        stopPollingInterval();
        verificationService.submitStep(VerificationStepsEnum.sso, nextState, verificationService.previousVerificationResponse);
    };

    const onCountdownFinish = () => {
        setShowCountDown(false);
    };

    const logo = getLogo();

    return (
        <div id="sid-step-pending" className="sid-l-container">
            { isPreviousStepSSO() ? (
                <SSOPendingComponent logo={logo} cancelSSO={cancelSSOStep} />
            ) : showCountDown ? (
                <CountDownComponent logo={logo} onCountdownFinish={onCountdownFinish} countdownSeconds={countdownSeconds} />
            ) : (
                <div>
                    <ReviewPendingComponent intl={intl} maxReviewTime={getMaxReviewTime(verificationService.programTheme)} logo={logo} />
                    <div className="sid-l-space-top-xl sid-pending-redirect-button">
                        <button
                            onClick={() => redirect('https://www.esri.com/en-us/home')}
                            className="sid-btn sid-btn--dark"
                            >
                            <FormattedHTMLMessage id="step.success.redirectButtonText" defaultMessage="Use code now" />
                        </button>
                    </div>
                </div>
            )}
            { showCountDown ? (
                <PoweredByComponent verificationService={verificationService} isCentered />
            ) : (
                <FormFooter verificationService={verificationService}>
                    <div /> {/* suppress default */}
                </FormFooter>
            )
        }

        </div>
    );
};

export const StepPendingComponent = injectIntl(StepPending);
